import { Card } from '@ordo/ui/card'
import { Skeleton } from '@ordo/ui/skeleton'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'

import { listStudentsNeedingRequiredInfo, type MyFamilyResponse, type StudentNeedingInfo } from '@/api/family'
import { CompleteStudentInfoSheet } from '@/components/family/complete-student-info-sheet'

/**
 * Plan 36 — Accept Invite landing page.
 *
 *   /accept-invite?token=<jwt>
 *
 * Reached via a magic-link emailed or SMS-sent to the recipient by
 * the invitation dispatcher (apps/subscription-runner). The page
 * immediately POSTs the token to `/api/invitations/accept`, which
 * calls `@ordo/auth.invitations.verifyAndConsume` (Multipass-backed)
 * + materializes Membership and writes the session cookie.
 *
 * Three states:
 *
 *   - **loading**: skeleton while the server round-trip is in flight.
 *   - **success**: brief "Welcome to Ordo!" then redirect to the
 *     ProfileCode-resolved landing page.
 *   - **error**: friendly state with the server's error code; cases:
 *       * missing token in URL
 *       * `invalid_token` (malformed)
 *       * `expired_or_invalid` (TTL elapsed)
 *       * `already_accepted` (link clicked twice; offer Sign In CTA)
 */

type Status = 'loading' | 'success' | 'complete-info' | 'error'

interface ErrorBody {
  error?: string
  message?: string
  cooldownLiftsAt?: string
}

export function AcceptInvitePage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<Status>('loading')
  const [error, setError] = useState<ErrorBody | null>(null)
  const [redirectTo, setRedirectTo] = useState<string>('/')
  // Claim-time gap-fill: students the accepted invite claimed/made visible
  // that still miss campus-REQUIRED facts — the completion sheet opens
  // before the redirect (skippable; ordering stays blocked until filled).
  const [studentsNeedingInfo, setStudentsNeedingInfo] = useState<StudentNeedingInfo[]>([])

  useEffect(() => {
    const token = params.get('token')
    if (!token) {
      setError({ error: 'invalid_token', message: 'Missing invitation token.' })
      setStatus('error')
      return
    }
    let cancelled = false
    void fetch('/api/invitations/accept', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ token }),
    })
      .then(async (res) => {
        if (cancelled) {
          return
        }
        if (res.ok) {
          const body = (await res.json().catch(() => ({}))) as { redirectTo?: string }
          setRedirectTo(body.redirectTo ?? '/')
          // The accept may have auto-claimed students (parent-provisioning
          // invites) — if any visible student misses REQUIRED facts, ask
          // for them now instead of redirecting straight away.
          let needingInfo: StudentNeedingInfo[] = []
          try {
            const familyRes = await fetch('/api/family/me', { credentials: 'include' })
            if (familyRes.ok) {
              needingInfo = listStudentsNeedingRequiredInfo((await familyRes.json()) as MyFamilyResponse)
            }
          } catch (err) {
            // Non-blocking — the /family banner is the fallback prompt.
            void err
          }
          if (cancelled) {
            return
          }
          if (needingInfo.length > 0) {
            setStudentsNeedingInfo(needingInfo)
            setStatus('complete-info')
            return
          }
          setStatus('success')
          // Brief celebrate-then-go interstitial.
          setTimeout(() => {
            if (!cancelled) {
              navigate(body.redirectTo ?? '/', { replace: true })
            }
          }, 1500)
          return
        }
        const body = (await res.json().catch(() => ({}))) as ErrorBody
        setError(body)
        setStatus('error')
      })
      .catch((err) => {
        if (cancelled) {
          return
        }
        setError({ error: 'network', message: err instanceof Error ? err.message : 'Network error' })
        setStatus('error')
      })
    return () => {
      cancelled = true
    }
  }, [params, navigate])

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="flex w-full max-w-sm flex-col gap-4 p-8 shadow-md">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="mt-2 h-10 w-full" />
        </Card>
      </div>
    )
  }

  if (status === 'success') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="flex w-full max-w-sm flex-col items-center gap-3 p-8 text-center shadow-md">
          <h1 className="text-2xl font-semibold tracking-tight">Welcome to Ordo</h1>
          <p className="text-sm text-muted-foreground">Taking you to {redirectTo}…</p>
        </Card>
      </div>
    )
  }

  if (status === 'complete-info') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="flex w-full max-w-sm flex-col items-center gap-3 p-8 text-center shadow-md">
          <h1 className="text-2xl font-semibold tracking-tight">Welcome to Ordo</h1>
          <p className="text-sm text-muted-foreground">One more step — your school needs a bit more student info.</p>
        </Card>
        <CompleteStudentInfoSheet
          open
          onOpenChange={(open) => {
            if (!open) {
              navigate(redirectTo, { replace: true })
            }
          }}
          students={studentsNeedingInfo}
        />
      </div>
    )
  }

  return <AcceptInviteError error={error} />
}

function AcceptInviteError({ error }: { error: ErrorBody | null }) {
  const code = error?.error ?? 'invalid_token'
  let title = 'We couldn’t accept this invitation'
  let body = error?.message ?? 'Please ask the sender to send a new invitation link.'
  let ctaLabel = 'Go to sign in'
  const ctaHref = '/signin'

  if (code === 'expired_or_invalid') {
    title = 'This invitation has expired'
    body =
      'Contact your school to request a new invitation. Each invitation is valid for 14 days from the most recent send.'
  } else if (code === 'already_accepted') {
    title = 'This invitation was already accepted'
    body = 'Sign in to your Ordo account instead.'
    ctaLabel = 'Sign in'
  } else if (code === 'not_found') {
    title = 'Invitation not found'
    body = 'The invitation may have been removed by your school. Contact them for a fresh link.'
  } else if (code === 'invalid_token') {
    title = 'Invalid invitation link'
    body =
      'The link looks malformed. Make sure you used the whole URL from the email; if you copy-pasted, try clicking it directly.'
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="flex w-full max-w-sm flex-col gap-4 p-8 shadow-md">
        <header className="text-center">
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{body}</p>
        </header>
        <a
          href={ctaHref}
          className="mt-2 inline-block w-full rounded-md bg-primary px-4 py-2 text-center text-sm font-medium text-primary-foreground hover:bg-primary/90">
          {ctaLabel}
        </a>
      </Card>
    </div>
  )
}
